/**
 * lookupFieldName — resolve a workflow field ID to a human-readable name.
 *
 * sl-g3on.5.3: The backend's i18n warnings (sl-g3on.5.2) carry raw field
 * IDs in their params. The frontend has the workflow definition in
 * `WorkflowSchema` and can map those IDs to display names. The lookup
 * walks the input schema (required + optional) and the output schema
 * Record. Returns `null` when the ID is unknown — callers should fall
 * back to the raw ID so debugging stays possible.
 *
 * Pure function, no React, no Y.js — testable in isolation.
 */

import type { WorkflowSchema } from '../../services/workflow';

export function lookupFieldName(
  schema: WorkflowSchema | null | undefined,
  fieldId: string | null | undefined
): string | null {
  if (!schema || !fieldId) return null;

  // Input fields — both required and optional are stored on the schema.
  for (const f of schema.input.required) {
    if (f.id === fieldId) {
      // WorkflowSchemaField uses `field` for the human-readable name.
      return f.field || fieldId;
    }
  }
  for (const f of schema.input.optional) {
    if (f.id === fieldId) {
      return f.field || fieldId;
    }
  }

  // Output fields — keyed by field ID, value carries `name`.
  const output = schema.output[fieldId];
  if (output) {
    return output.name || fieldId;
  }

  return null;
}
